import { useRef, useState, type ChangeEvent, type MutableRefObject } from 'react'
import type { Control } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import type { UseFormRegisterReturn } from 'react-hook-form'
import { cn } from '@lib/utils/helpers'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { RESIDENT_DOCUMENT_FILE_ACCEPT } from '../../constants/residentConstants'
import type { ResidentDefaultFormValues } from '../../types/resident'
import { PaymentLedgerFinalLedgerDialog } from './PaymentLedgerFinalLedgerDialog'
import { ResidentFileUploadField } from './ResidentFileUploadField'

const PAYMENT_LEDGER_FILE_FIELD_TEST_ID = 'resident-payment-ledger-file-field'

/**
 * Payment ledger file row. When `skipPrompt` is false, matches Angular `uploadFile` for `payment_ledger`:
 * after choosing a file, opens “Payment Ledger / Is this the Final Ledger?” + NOTE, then uploads.
 */
export function PaymentLedgerFileField({
  control,
  disabled,
  skipPrompt,
  registerReturn,
  uploading = false,
  onImmediateUpload,
}: {
  control: Control<ResidentDefaultFormValues>
  disabled: boolean
  skipPrompt: boolean
  registerReturn: UseFormRegisterReturn
  uploading?: boolean
  /** Uploads immediately after Final Ledger Yes/No (or right away when `skipPrompt`). */
  onImmediateUpload?: (files: File[], isFinalLedger: boolean | null) => void | Promise<void>
}) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [chosenDisplayName, setChosenDisplayName] = useState('')
  const [pendingLargeFile, setPendingLargeFile] = useState<{
    files: File[]
  } | null>(null)
  const pendingFilesRef = useRef<File[]>([])
  const inputRef = useRef<HTMLInputElement | null>(null)

  if (skipPrompt) {
    return (
      <ResidentFileUploadField
        disabled={disabled}
        registerReturn={registerReturn}
        uploading={uploading}
        aria-label="Upload payment ledger"
        dataTestId={PAYMENT_LEDGER_FILE_FIELD_TEST_ID}
        accept={RESIDENT_DOCUMENT_FILE_ACCEPT}
        onImmediateUpload={
          onImmediateUpload
            ? (files) => onImmediateUpload(files, null)
            : undefined
        }
      />
    )
  }

  const busy = disabled || uploading

  return (
    <Controller
      name="payment_ledger"
      control={control}
      render={({ field: { onChange, onBlur, ref, name } }) => {
        const setRefs = (el: HTMLInputElement | null) => {
          inputRef.current = el
          if (typeof ref === 'function') ref(el)
          else if (ref && typeof ref === 'object' && 'current' in ref) {
            ;(ref as MutableRefObject<HTMLInputElement | null>).current = el
          }
        }

        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
          // Snapshot files BEFORE clearing the input — `input.files` is a live FileList
          // and becomes empty when `value` is reset (otherwise Yes/No uploads nothing).
          const files = e.target.files?.length ? Array.from(e.target.files) : []
          e.target.value = ''
          if (!files.length) {
            onChange(e)
            setChosenDisplayName('')
            pendingFilesRef.current = []
            return
          }

          let largeFileText = ''
          const tenMb = 10 * 1024 * 1024
          for (let i = 0; i < files.length; i++) {
            if (files[i].size > tenMb) {
              largeFileText += `\n${files[i].name} - ${(files[i].size / 1024 / 1024).toFixed(2)} MB`
            }
          }
          if (largeFileText) {
            const confirmMessage = `WARNING: Your file is larger than 10MB.\nWe request you try to compress the file so it takes less storage.\nHowever, if you are not able to compress it further, please continue.\nDo you want to continue?`
            if (!window.confirm(confirmMessage)) {
              pendingFilesRef.current = []
              return
            }
          }

          pendingFilesRef.current = files
          setDialogOpen(true)
        }

        const handleConfirmLargeFile = () => {
          setPendingLargeFile(null)
          setDialogOpen(true)
        }

        const handleCancelLargeFile = () => {
          setPendingLargeFile(null)
          pendingFilesRef.current = []
          setChosenDisplayName('')
        }

        const confirmChoice = async (isFinal: boolean) => {
          const files = pendingFilesRef.current
          pendingFilesRef.current = []
          setDialogOpen(false)
          const file = files[0]
          if (!file) return
          setChosenDisplayName(file.name)
          if (onImmediateUpload) {
            try {
              await onImmediateUpload([file], isFinal)
              setChosenDisplayName('')
              onChange({
                target: { name, value: '', files: null },
                type: 'change',
              } as unknown as ChangeEvent<HTMLInputElement>)
            } catch {
              // Parent surfaces API errors.
            }
            return
          }
          const dt = new DataTransfer()
          dt.items.add(file)
          const synthetic = { target: { files: dt.files } } as ChangeEvent<HTMLInputElement>
          onChange(synthetic)
        }

        const dismissDialog = () => {
          void confirmChoice(false)
        }

        const showText = uploading
          ? 'Uploading…'
          : chosenDisplayName.trim() || 'Choose file...'

        return (
          <>
            <div
              className={cn(
                'flex min-h-[2.75rem] items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/50 p-1.5',
              )}
              data-testid={PAYMENT_LEDGER_FILE_FIELD_TEST_ID}
            >
              <button
                type="button"
                disabled={busy}
                aria-label="Upload payment ledger"
                onClick={() => inputRef.current?.click()}
                data-testid={`${PAYMENT_LEDGER_FILE_FIELD_TEST_ID}-choose`}
                className={cn(
                  'min-w-0 flex-1 truncate rounded-md px-2 py-1.5 text-left text-sm transition',
                  chosenDisplayName.trim() || uploading ? 'text-slate-700' : 'text-slate-400',
                  'hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(21_151_255)]/40 disabled:cursor-not-allowed disabled:opacity-60',
                )}
              >
                {showText}
              </button>
              <input
                ref={setRefs}
                type="file"
                name={name}
                accept={RESIDENT_DOCUMENT_FILE_ACCEPT}
                onBlur={onBlur}
                onChange={handleChange}
                className="sr-only"
                disabled={busy}
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
                data-testid={`${PAYMENT_LEDGER_FILE_FIELD_TEST_ID}-upload`}
                className="shrink-0 rounded-md bg-[rgb(21_151_255)] px-5 py-1.5 text-sm font-semibold text-white transition hover:bg-[rgb(13_122_204)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(21_151_255)]/40 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>

            <PaymentLedgerFinalLedgerDialog
              isOpen={dialogOpen}
              onDismiss={dismissDialog}
              onChoose={(isFinal) => {
                void confirmChoice(isFinal)
              }}
            />

            <ConfirmDialog
              isOpen={pendingLargeFile != null}
              title="Large File"
              message={
                'WARNING: Your file is larger than 10MB.\n' +
                'We request you try to compress the file so it takes less storage.\n' +
                'However, if you are not able to compress it further, please continue.\n' +
                'Do you want to continue?'
              }
              primaryLabel="Yes"
              onPrimary={handleConfirmLargeFile}
              secondaryLabel="No"
              onSecondary={handleCancelLargeFile}
              onDismiss={handleCancelLargeFile}
              messageLayout="largeFile"
              primaryFirst
              actionStyle="application"
              sectionedChrome
            />
          </>
        )
      }}
    />
  )
}
